<template>
  <div class="kanban-column" :class="{ 'kanban-column-collapsed': collapsed }">
    <div
      v-if="collapsed"
      class="kanban-column-collapsed-bar"
      :style="{ backgroundColor: column.color, color: collapsedTextColor }"
      @click="setColumnCollapsed({ columnId: column.id, collapsed: false })"
    >
      <div class="kanban-column-collapsed-toggle" title="Expandir coluna">
        <i class="fa-solid fa-angles-right"></i>
      </div>
      <span class="kanban-column-collapsed-title">{{ column.name }}</span>
      <span class="kanban-column-collapsed-count" :style="{ backgroundColor: collapsedBadgeBackground }">{{ column.cards.length }}</span>
    </div>

    <template v-else>
      <div class="kanban-column-header">
        <div class="kanban-column-color-trigger" @click="$emit('edit-color', column)">
          <div
            class="kanban-column-color-dot"
            :style="{ backgroundColor: column.color }"
          ></div>
        </div>

        <InputTextEdit
          class="kanban-column-title-input"
          :enter="(item) => $emit('rename', item)"
          :params="column"
          v-model="column.name"
        />

        <div
          class="kanban-column-collapse"
          title="Retrair coluna"
          @click="setColumnCollapsed({ columnId: column.id, collapsed: true })"
        >
          <i class="fa-solid fa-angles-left"></i>
        </div>

        <div
          v-if="column.cards.length === 0"
          class="kanban-column-delete"
          @click="$emit('delete', column)"
        >
          <i class="fa-solid fa-trash"></i>
        </div>
      </div>

      <div v-if="hasRule('Rental') && column.showValue" class="kanban-column-totals">
        <div v-if="!loadingTotals" class="kanban-column-totals-row">
          <span class="kanban-column-total-pill">
            <i class="fas fa-exchange-alt"></i> {{ column.totalRent }}
          </span>
          <span class="kanban-column-total-pill">
            {{ column.totalValue | currency }}
          </span>
        </div>
        <div v-else class="kanban-column-totals-loading">
          <span class="kanban-column-totals-loading-text">Calculando...</span>
        </div>
      </div>

      <div class="kanban-column-divider"></div>

      <div class="kanban-column-add-card" @click="$emit('add-card', column)">
        <i class="fa-solid fa-circle-plus"></i>
        <span>Cartão</span>
      </div>

      <ScrollBar
        :minHeight="columnHeight"
        :maxHeight="columnHeight"
      >
        <div class="kanban-column-cards">
          <draggable
            :list="column.cards"
            :animation="200"
            ghost-class="kanban-column-ghost"
            group="cards"
            @end="(evt) => $emit('card-dropped', evt)"
          >
            <KanbanCardItem
              v-for="card in column.cards"
              :key="card.id"
              :card="card"
              @open="$emit('open-card', $event)"
            />
          </draggable>
        </div>
      </ScrollBar>
    </template>
  </div>
</template>

<script>
import ScrollBar from "@/ui/layout/ScrollBar.vue";

import InputTextEdit from "@/ui/forms/InputTextEdit.vue";

import draggable from "vuedraggable";
import KanbanCardItem from "./KanbanCardItem.vue";

import { mapGetters, mapMutations } from "vuex";

export default {
  name: "KanbanColumn",
  components: { InputTextEdit, ScrollBar, draggable, KanbanCardItem },
  props: {
    column: {
      type: Object,
      required: true,
    },
    columnHeight: {
      type: Number,
      default: 500,
    },
    loadingTotals: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters("user", ["hasRule"]),
    ...mapGetters("planner", ["isColumnCollapsed"]),
    collapsed() {
      return this.isColumnCollapsed(this.column.id);
    },
    collapsedTextColor() {
      const hex = (this.column.color || "").replace("#", "");
      if (hex.length !== 6) return "#ffffff";
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.6 ? "#1a1a1a" : "#ffffff";
    },
    collapsedBadgeBackground() {
      return this.collapsedTextColor === "#1a1a1a" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.25)";
    },
  },
  methods: {
    ...mapMutations("planner", ["setColumnCollapsed"]),
  },
};
</script>

<style scoped>
.kanban-column {
  min-width: 320px;
  max-width: 320px;
  margin-right: 16px;
  padding: 16px;
  border-radius: 12px;
  background: var(--bg-base);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  margin-bottom: 24px;
}

.kanban-column-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.kanban-column-color-trigger {
  cursor: pointer;
  flex-shrink: 0;
  padding: 2px;
  border-radius: 999px;
  transition: opacity 0.15s;
}

.kanban-column-color-trigger:hover {
  opacity: 0.7;
}

.kanban-column-color-dot {
  width: 14px;
  height: 14px;
  border-radius: 999px;
}

.kanban-column-title-input {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
}

.kanban-column-delete {
  font-size: 14px;
  color: var(--color-danger);
  cursor: pointer;
  opacity: 0.5;
  flex-shrink: 0;
  transition: opacity 0.15s;
  animation: fadeIn 0.6s ease forwards;
}

.kanban-column-delete:hover {
  opacity: 1;
}

.kanban-column-collapse {
  font-size: 13px;
  color: var(--text-secondary);
  cursor: pointer;
  opacity: 0.6;
  flex-shrink: 0;
  padding: 2px 4px;
  border-radius: 6px;
  transition: opacity 0.15s, background 0.15s;
}

.kanban-column-collapse:hover {
  opacity: 1;
  background: var(--bg-elevated);
}

.kanban-column-collapsed {
  min-width: 44px;
  max-width: 44px;
  padding: 0;
  border: none;
  box-shadow: none;
  background: transparent;
}

.kanban-column-collapsed-bar {
  width: 100%;
  min-height: 220px;
  border-radius: 12px;
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}

.kanban-column-collapsed-toggle {
  font-size: 13px;
}

.kanban-column-collapsed-title {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.kanban-column-collapsed-count {
  font-size: 12px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 999px;
  padding: 2px 8px;
}

.kanban-column-totals {
  margin-bottom: 8px;
}

.kanban-column-totals-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.kanban-column-total-pill {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  background: var(--bg-elevated);
  border-radius: 999px;
  padding: 3px 10px;
}

.kanban-column-totals-loading {
  padding: 4px 0;
}

.kanban-column-totals-loading-text {
  font-size: 12px;
  color: var(--text-muted);
  font-style: italic;
}

.kanban-column-divider {
  height: 1px;
  background: var(--border-subtle);
  margin-bottom: 10px;
}

.kanban-column-add-card {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-brand);
  cursor: pointer;
  margin-bottom: 10px;
  padding: 4px 6px;
  border-radius: 8px;
  transition: background 0.15s;
}

.kanban-column-add-card:hover {
  background: var(--bg-elevated);
}

.kanban-column-cards {
  padding-right: 4px;
}

::v-deep .kanban-column-ghost {
  opacity: 0.4;
  background: var(--bg-elevated);
  border-radius: 12px;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 0.5; }
}
</style>





